import "react-notifications-component/dist/theme.css";
import "app/config/global";

import { MsalAuthenticationTemplate, MsalProvider } from "@azure/msal-react";
import { QueryCache, QueryClient, QueryClientProvider } from "react-query";
import React, { useCallback, useEffect, useState } from "react";
import { clearToken, getMicrosoftSecurityToken, setEndpoints } from "app/auth/authService";

import AppErrorBoundaryWrapper from "app/error-handling/AppErrorBoundary";
import { AppErrorProvider } from "app/contexts/AppErrorContext";
import AppRoutes from "app/router/AppRoutes";
import { BrowserRouter } from "react-router-dom";
import Build from "app/build";
import { ErrorComponent } from "shared/components/feedback/error/ErrorComponent";
import GlobalStyle from "app/styles/Global";
import Grid from "@material-ui/core/Grid";
import HelveticaNow from "shared/assets/HelveticaNow";
import InstallPWA from "app/pwa/InstallPWA";
import { InteractionType } from "@azure/msal-browser";
import MaintenancePage from "app/pages/MaintenancePage";
import { PageLayout } from "app/components/PageLayout";
import PropTypes from "prop-types";
import { ReactNotifications } from "react-notifications-component";
import { SessionProvider } from "app/contexts/SessionContext";
import Theme from "app/styles/Theme";
import { ThemeProvider } from "@material-ui/core/styles";
import env from "react-dotenv";
import { loginRequest } from "app/auth/authConfig";
import { useErrors } from "shared/hooks/errors/useErrors";

function App({ pca }) {
    const [loading, setLoading] = useState(true);
    const authRequest = { ...loginRequest };
    const { handleError } = useErrors();

    //global setup, it's not necessary to set refetchOnWindowFocus in each call
    //if in some cases we need it as true, set in the query, not here
    const queryClient = new QueryClient({
        defaultOptions: {
            queries: {
                refetchOnWindowFocus: false,
                cacheTime: 0,
            },
        },
        queryCache: new QueryCache({
            onError: (_error, query) => {
                if (query?.state?.error?.body) {
                    handleError(query.state.error.body);
                }
            },
        }),
    });

    const logout = useCallback(() => {
        pca.logout();
        clearToken();
    }, [pca]);

    useEffect(() => {
        const initialize = async () => {
            await setEndpoints();
            await getMicrosoftSecurityToken(pca);
            setLoading(false);

            const failmsg = "The app can't reload the page";
            try {
                const feVersion = await Build.get();
                let localVersion = localStorage.getItem("app-version");

                if (!localVersion && feVersion?.version) {
                    localVersion = feVersion.version;
                    localStorage.setItem("app-version", localVersion);
                }
                if (localVersion !== feVersion?.version) {
                    localStorage.setItem("app-version", feVersion.version);
                    logout();
                }
                if (!feVersion) {
                    console.log(failmsg);
                }
            } catch {
                console.log(failmsg);
            }
        };

        initialize();
    }, [logout, pca]);

    const layout = (
        <>
            <AppErrorBoundaryWrapper>
                <AppErrorProvider>
                    <QueryClientProvider client={queryClient}>
                        <BrowserRouter>
                            <ThemeProvider theme={Theme}>
                                <MsalProvider instance={pca}>
                                    <MsalAuthenticationTemplate
                                        interactionType={
                                            InteractionType.Redirect
                                        }
                                        authenticationRequest={authRequest}
                                        errorComponent={ErrorComponent}
                                    >
                                        <GlobalStyle />
                                        <HelveticaNow />
                                        <ReactNotifications></ReactNotifications>
                                        <SessionProvider>
                                            <PageLayout>
                                                <Grid
                                                    container
                                                    justifyContent="center"
                                                >
                                                    <AppRoutes />
                                                </Grid>
                                            </PageLayout>
                                        </SessionProvider>
                                        <InstallPWA />
                                    </MsalAuthenticationTemplate>
                                </MsalProvider>
                            </ThemeProvider>
                        </BrowserRouter>
                    </QueryClientProvider>
                </AppErrorProvider>
            </AppErrorBoundaryWrapper>
            <div id="divAppErrorModal"></div>
        </>
    );

    return env.UNDER_MAINTENANCE === "true"
        ? <MaintenancePage />
        : !loading && layout;
}

App.propTypes = {
    pca: PropTypes.object.isRequired
};

export default App;
